// Drives the collapsible-content accordion: smooth open/close (a
// max-height transition on .accordion__content, driven off a real measured
// pixel value below) instead of <details>'s native instant show/hide, and
// closes any other open row in the same section when one opens.
//
// max-height is transitioned to/from a concrete pixel value measured via
// scrollHeight, not to `1fr`/`auto` — animating a flexible size (CSS grid's
// `fr` unit included) forces the browser to keep recomputing the content's
// intrinsic size on every single frame of the transition, which is what
// made an earlier version of this feel jerky/stuttery instead of smooth.
// A fixed pixel target is just a normal, cheap, GPU-friendly interpolation.
//
// The native <details>/<summary> markup stays as the accessible, no-JS
// fallback (it still opens/closes, just instantly) — this only intercepts
// the click to animate on top of it. `open` itself is still what the
// element's real state as far as assistive tech is concerned; on close we
// deliberately keep it set until the collapse transition finishes so the
// content doesn't just vanish (display: none) mid-animation, then remove it.
//
// `row.isOpen` is the source of truth for "what the user last asked for",
// tracked separately from `details.open`/the `is-open` class because both
// of those deliberately lag behind during a close animation (see above) —
// using either of them to decide what a click should do next means a click
// during a close animation reads as "still open" and closes it *again*
// instead of reopening it. Any in-flight rAFs/transitionend listener from a
// row's previous open()/close() call are also explicitly cancelled at the
// start of the next one, so an interrupted animation can't later clobber
// the one that superseded it.
(function () {
  if (window.customElements.get('accordion-group')) return;

  class AccordionGroup extends HTMLElement {
    connectedCallback() {
      this.rows = Array.from(this.querySelectorAll(':scope > .accordion')).map((wrapper) => {
        const details = wrapper.querySelector(':scope > details');
        return {
          details,
          summary: wrapper.querySelector(':scope > details > summary'),
          content: wrapper.querySelector(':scope > details > .accordion__content'),
          isOpen: !!(details && details.open),
          rafId: null,
          onTransitionEnd: null,
        };
      });

      this.rows.forEach((row) => {
        if (!row.details || !row.summary || !row.content) return;
        row.summary.addEventListener('click', (event) => this.onSummaryClick(event, row));
      });
    }

    onSummaryClick(event, row) {
      event.preventDefault();

      if (row.isOpen) {
        this.close(row);
      } else {
        this.rows.forEach((other) => {
          if (other !== row && other.isOpen) this.close(other);
        });
        this.open(row);
      }
    }

    get reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    cancelPending(row) {
      if (row.rafId) cancelAnimationFrame(row.rafId);
      row.rafId = null;
      if (row.onTransitionEnd) {
        row.content.removeEventListener('transitionend', row.onTransitionEnd);
        row.onTransitionEnd = null;
      }
    }

    open(row) {
      this.cancelPending(row);
      row.isOpen = true;
      row.details.open = true;
      row.summary.setAttribute('aria-expanded', 'true');
      row.content.classList.add('is-open');

      if (this.reducedMotion) {
        row.content.style.maxHeight = 'none';
        return;
      }

      // Full natural height of the content, regardless of the 0px max-height
      // currently clipping it — scrollHeight always reports the unclipped size.
      const targetHeight = row.content.scrollHeight;

      // Set a concrete starting pixel value (not 0 -> "none", which can't be
      // interpolated) before switching to the target, so the transition
      // actually has two real values to animate between. A single rAF isn't
      // reliably enough for the browser to have committed that starting
      // value before the target write lands — `open` was *just* set true
      // this same tick, so the two can get coalesced into one frame (the
      // whole row just snaps open, no animation). Two rAFs guarantees the
      // 0px frame actually lands first.
      row.content.style.maxHeight = '0px';

      row.rafId = requestAnimationFrame(() => {
        row.rafId = requestAnimationFrame(() => {
          row.rafId = null;
          row.content.style.maxHeight = `${targetHeight}px`;
        });
      });

      row.onTransitionEnd = (event) => {
        if (event.target !== row.content || event.propertyName !== 'max-height') return;
        row.content.removeEventListener('transitionend', row.onTransitionEnd);
        row.onTransitionEnd = null;
        // Rest at `none` (rather than staying pinned to the measured pixel
        // value) so content that grows afterwards — a window resize
        // reflowing text, an image finishing loading, block content edited
        // in the theme editor — never ends up clipped.
        if (row.isOpen) row.content.style.maxHeight = 'none';
      };
      row.content.addEventListener('transitionend', row.onTransitionEnd);
    }

    close(row) {
      this.cancelPending(row);
      row.isOpen = false;
      row.summary.setAttribute('aria-expanded', 'false');

      if (this.reducedMotion) {
        row.content.classList.remove('is-open');
        row.content.style.maxHeight = '';
        row.details.open = false;
        return;
      }

      // maxHeight is currently `none` (at rest, open) — pin it to the exact
      // current pixel height first so there's a concrete value to transition
      // away from. Same two-rAF reasoning as open().
      row.content.style.maxHeight = `${row.content.scrollHeight}px`;

      row.rafId = requestAnimationFrame(() => {
        row.rafId = requestAnimationFrame(() => {
          row.rafId = null;
          row.content.classList.remove('is-open');
          row.content.style.maxHeight = '0px';
        });
      });

      row.onTransitionEnd = (event) => {
        if (event.target !== row.content || event.propertyName !== 'max-height') return;
        row.content.removeEventListener('transitionend', row.onTransitionEnd);
        row.onTransitionEnd = null;
        // The row may have been reopened before this closing transition finished.
        if (!row.isOpen) row.details.open = false;
      };
      row.content.addEventListener('transitionend', row.onTransitionEnd);
    }
  }

  window.customElements.define('accordion-group', AccordionGroup);
})();
